import React, { useCallback, useState, Suspense, lazy } from 'react';
import { Hero } from './components/Hero';
import { UpsellProvider } from './components/UpsellContext';
import type { UpsellProduct } from './components/UpsellModal';
import { appendAttributionParams } from './utils/attribution';
// ----------------------------------------------------------------
// Code-splitting: everything below the hero is lazy-loaded so the
// initial JS bundle only contains what's needed to paint the first
// screen (Hero). This lets the LCP element render much sooner.
// Named exports are mapped to default for lazy.
// ----------------------------------------------------------------
const Problem = lazy(() => import('./components/Problem').then(m => ({
  default: m.Problem
})));
const WhyNow = lazy(() => import('./components/WhyNow').then(m => ({
  default: m.WhyNow
})));
const Mechanism = lazy(() => import('./components/Mechanism').then(m => ({
  default: m.Mechanism
})));
const InsideGuide = lazy(() => import('./components/InsideGuide').then(m => ({
  default: m.InsideGuide
})));
const HowItWorks = lazy(() => import('./components/HowItWorks').then(m => ({
  default: m.HowItWorks
})));
const Benefits = lazy(() => import('./components/Benefits').then(m => ({
  default: m.Benefits
})));
const Bonuses = lazy(() => import('./components/Bonuses').then(m => ({
  default: m.Bonuses
})));
const Testimonials = lazy(() => import('./components/Testimonials').then(m => ({
  default: m.Testimonials
})));
const FAQ = lazy(() => import('./components/FAQ').then(m => ({
  default: m.FAQ
})));
const FinalOffer = lazy(() => import('./components/FinalOffer').then(m => ({
  default: m.FinalOffer
})));
const StickyCTA = lazy(() => import('./components/StickyCTA').then(m => ({
  default: m.StickyCTA
})));
const UpsellModal = lazy(() => import('./components/UpsellModal').then(m => ({
  default: m.UpsellModal
})));
// ============================================================
// SHOPIFY CART CONFIG — Edit variant IDs and prices here
// ============================================================
const SHOPIFY_CART_DOMAIN = 'https://ebookonline.lat/cart/';
const MAIN_PRODUCT_VARIANT_ID = '50885396988071';
const MAIN_PRODUCT_PRICE = 299.99;
const UPSELL_PRODUCTS: UpsellProduct[] = [{
  id: 'bienestar-natural-despues-50',
  name: 'BIENESTAR NATURAL DESPUÉS DE LOS 50',
  variantId: '50885403181223',
  normalPrice: 79.99,
  offerPrice: 39.99,
  benefit: 'Guía práctica para cuidar energía, movilidad, digestión y bienestar general después de los 50.',
  image: 'https://cdn.magicpatterns.com/uploads/vhXpUvZnM56RapJHLgLaj6/ChatGPT_Image_3_jun_2026,_18_31_28.webp'
}, {
  id: 'secretos-naturales-abuela',
  name: 'SECRETOS NATURALES DE LA ABUELA',
  variantId: '50885399380135',
  normalPrice: 79.99,
  offerPrice: 39.99,
  benefit: 'Trucos naturales, recetas caseras y consejos tradicionales para el hogar, el cuerpo y el bienestar diario.',
  image: 'https://cdn.magicpatterns.com/uploads/nVg4aiw29hDYFdQPERGgKy/ChatGPT_Image_3_jun_2026,_18_30_02.webp'
}, {
  id: '700-hierbas-medicinales',
  name: '700 HIERBAS MEDICINALES ENCICLOPEDIA',
  variantId: '50885398626471',
  normalPrice: 79.99,
  offerPrice: 39.99,
  benefit: 'Una enciclopedia completa para conocer hierbas, usos tradicionales y preparaciones naturales.',
  image: 'https://cdn.magicpatterns.com/uploads/2PGVbzwrnMWyBLaJFXXjhr/ChatGPT_Image_3_jun_2026,_18_35_39.webp'
}];
// ---- BUNDLE (3 ebooks pack) ----
// IMPORTANT: For Shopify to actually charge $12.990 instead of 3 × $5.990 = $17.970,
// you must either:
//   (a) Create a "Bundle 3 ebooks" product in Shopify priced at $12.990 and put its
//       variant ID below in BUNDLE_VARIANT_ID, OR
//   (b) Add a Shopify automatic discount that applies when the 3 variants are in cart.
// If BUNDLE_VARIANT_ID is left empty, the checkout will fall back to the 3 individual
// variants (which would total $17.990 on Shopify's side).
const BUNDLE_PRICE = 99.99;
const BUNDLE_COMPARE_PRICE = 119.97; // "Antes" price shown in the bundle card (3 × $39.99)
const BUNDLE_VARIANT_ID = '50885416288423'; // Single Shopify variant for the 3-ebook pack (Biblioteca Premium de Bienestar Natural)
// --------------------------------
function buildCheckoutUrl(selectedVariantIds: string[], isBundle = false) {
  const items = [`${MAIN_PRODUCT_VARIANT_ID}:1`];
  if (isBundle && BUNDLE_VARIANT_ID) {
    items.push(`${BUNDLE_VARIANT_ID}:1`);
  } else {
    selectedVariantIds.forEach(variantId => {
      items.push(`${variantId}:1`);
    });
  }
  const url = `${SHOPIFY_CART_DOMAIN}${items.join(',')}`;
  // Forward fbclid + utm_* params so Meta attribution survives the jump to Shopify.
  return appendAttributionParams(url);
}
// ============================================================
export function App() {
  const [isUpsellOpen, setIsUpsellOpen] = useState(false);
  const handleOpenUpsell = useCallback(() => {
    setIsUpsellOpen(true);
  }, []);
  const handleCloseUpsell = useCallback(() => {
    setIsUpsellOpen(false);
  }, []);
  return <UpsellProvider onOpen={handleOpenUpsell} data-id="element-0">
      <div className="min-h-screen bg-cream-100 font-sans text-ink-900 selection:bg-olive-200 selection:text-ink-900" data-id="element-1">
        <main data-id="element-2">
          <Hero data-id="element-3" />
          <Suspense fallback={null} data-id="element-4">
            <Problem data-id="element-5" />
            <WhyNow data-id="element-6" />
            <Mechanism data-id="element-7" />
            <InsideGuide data-id="element-8" />
            <HowItWorks data-id="element-9" />
            <Benefits data-id="element-10" />
            <Bonuses data-id="element-11" />
            <Testimonials data-id="element-12" />
            <FAQ data-id="element-13" />
            <FinalOffer data-id="element-14" />
          </Suspense>
        </main>

        <Suspense fallback={null} data-id="element-15">
          <StickyCTA data-id="element-16" />
        </Suspense>

        <footer className="bg-cream-200 text-ink-600 py-10 pb-28 md:pb-10 px-5 text-center text-xs md:text-sm" data-id="element-17">
          <div className="max-w-3xl mx-auto space-y-3" data-id="element-18">
            <p className="font-serif text-base text-ink-800 italic" data-id="element-19">
              Biblioteca Natural · Más de 1,500 soluciones naturales
            </p>
            <p className="leading-relaxed" data-id="element-20">
              Esta guía es material educativo e informativo. No reemplaza el
              consejo, diagnóstico ni tratamiento de un profesional de la salud.
              Ante cualquier síntoma persistente, consulta con un médico.
            </p>
            <p className="pt-3" data-id="element-21">
              © {new Date().getFullYear()} · Todos los derechos reservados.
            </p>
          </div>
        </footer>

        {isUpsellOpen && <Suspense fallback={null} data-id="element-22">
            <UpsellModal isOpen={isUpsellOpen} onClose={handleCloseUpsell} mainPrice={MAIN_PRODUCT_PRICE} upsellProducts={UPSELL_PRODUCTS} bundlePrice={BUNDLE_PRICE} bundleComparePrice={BUNDLE_COMPARE_PRICE} buildCheckoutUrl={buildCheckoutUrl} data-id="element-23" />
          </Suspense>}
      </div>
    </UpsellProvider>;
}